import {
  FORMAT_AAC,
  FORMAT_M3U8,
  FORMAT_M4A,
  FORMAT_MP3,
  FORMAT_MP4,
  FORMAT_OGA,
  FORMAT_OGG,
  FORMAT_OPUS,
  FORMAT_WEBM,
  QUALITY_HIGH,
  QUALITY_LOW,
  QUALITY_MEDIUM
} from '../constants';
import EventTarget from '../utils/event-target';
import logger from '../../utils/logger';
import sourceExtension from '../utils/source-extension';
import environment from '../utils/environment';

const defaultOptions = {
  qualityLevel: QUALITY_MEDIUM
};

const defaultQualityOrder = [
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  QUALITY_LOW
];

const defaultFormatOrder = (function () {
  // iOS currently has trouble playing our m4a files where
  // it takes upwards of 20s to start playback, so we force
  // mp3 playback on those environments
  if (environment.platform.isIOS) {
    return [
      FORMAT_M3U8,
      FORMAT_AAC,
      FORMAT_OPUS,
      FORMAT_WEBM,
      FORMAT_OGA,
      FORMAT_OGG,
      FORMAT_MP3,
      FORMAT_MP4,
      FORMAT_M4A
    ];
  }

  return [
    FORMAT_M3U8,
    FORMAT_AAC,
    FORMAT_MP4,
    FORMAT_M4A,
    FORMAT_OPUS,
    FORMAT_WEBM,
    FORMAT_OGA,
    FORMAT_OGG,
    FORMAT_MP3
  ];
})();

/**
 * Sorting function used to sort audio sources in the preferred format order
 *
 * @param {object} sourceA An audio source object
 * @param {object} sourceB An audio source object
 *
 * @return {number}
 */
function formatSorter (sourceA, sourceB) {
  const formatA = sourceExtension(sourceA);
  const formatB = sourceExtension(sourceB);
  const indexA = defaultFormatOrder.indexOf(formatA);
  const indexB = defaultFormatOrder.indexOf(formatB);

  if (indexA === -1 || indexA > indexB) {
    return 1;
  }

  if (indexA < indexB) {
    return -1;
  }

  return 0;
}

/**
 * Returns a function which can be used to sort a list of levels by a quality order array
 *
 * @param {array} qualityOrder A list of quality labels in the desired order
 *
 * @return {function}
 */
const qualitySorter = function (qualityOrder) {
  return function (levelA, levelB) {
    const qualityA = levelA.label;
    const qualityB = levelB.label;
    const bitrateA = levelA.bitrate;
    const bitrateB = levelB.bitrate;

    // get the index of the the levels quality label in the quality order array
    const indexA = qualityOrder.indexOf(qualityA);
    const indexB = qualityOrder.indexOf(qualityB);

    // as long as we get an index for both levels, compare them and order accordingly
    if (indexA !== -1 && indexB !== -1) {
      if (indexA > indexB) {
        return 1;
      }

      if (indexA < indexB) {
        return -1;
      }
    }

    // if the indexes of both levels match, then compare their bitrate, sorting highest to lowest
    return bitrateB - bitrateA;
  };
};

/**
 * Resolve an ordered list of supported audio sources
 *
 * @param {array} sources List of available audio sources
 *
 * @return {array}
 */
const getSupportedSources = function (sources) {
  const supportedSources = sources.reduce((sources, source) => {
    if (this.player.canPlaySource(source)) {
      sources.push(source);
    }

    return sources;
  }, []);

  supportedSources.sort(formatSorter);

  return supportedSources;
};

/**
 * Resolve a list of available quality levels from a list of sources
 *
 * @param {array} sources List of audio source objects
 *
 * @return {array} List of available quality levels
 */
const getQualityLevels = function (sources) {
  const qualityLevelsFound = {};
  const qualityLevels = sources.reduce((levels, source) => {
    // Uncomment to include levels with the same label but different bitrates
    // const qualityKey = `${source.quality}:${source.bitrate}`;
    const qualityKey = source.quality;

    if (typeof qualityLevelsFound[qualityKey] === 'undefined') {
      qualityLevelsFound[qualityKey] = levels.push({
        label: source.quality,
        bitrate: source.bitrate,
        sources: [source]
      }) - 1;
    } else {
      levels[qualityLevelsFound[qualityKey]].sources.push(source);
    }

    return levels;
  }, []);

  return qualityLevels;
};

export default class LevelsController extends EventTarget {
  constructor (player, options = {}) {
    super();

    this.player = player;
    this.options = options;
    this.logger = options.logger || logger;

    this.currentQualityLevels = [];
    this.currentQualityIndex = -1;
  }

  /**
   * Destroy the controller
   */
  destroy () {
    this.clearLevels();
  }

  /**
   * Clear the currently loaded levels list
   */
  clearLevels () {
    this.currentQualityLevels = [];
    this.currentQualityIndex = -1;
  }

  /**
   * Retrieve the current active level
   *
   * @return {object}
   */
  getCurrentLevel () {
    return this.currentQualityLevels[this.currentQualityIndex];
  }

  /**
   * Retrieve the label of the currently selected quality level
   *
   * @return {string}
   */
  getQualityLevel () {
    return this.currentQualityIndex !== -1
      ? this.currentQualityLevels[this.currentQualityIndex].label
      : this.options.qualityLevel;
  }

  /**
   * Retrieve the index of the current quality level
   *
   * @return {number}
   */
  getQualityLevelIndex () {
    return this.currentQualityIndex;
  }

  /**
   * Return the list of available quality levels
   *
   * @return {array}
   */
  getQualityLevels () {
    return this.currentQualityLevels.filter(function (level) {
      return level.label;
    });
  }

  /**
   * Change the current quality level
   *
   * @param {string} label The label of the quality level
   */
  setQualityLevel (label) {
    // return early if the current quality is the desired quality
    if (this.getQualityLevel() === label) {
      return;
    }

    // find a level in the preferred quality
    let chosenLevelIndex = -1;

    for (let i = 0, l = this.currentQualityLevels.length; i < l; i++) {
      if (this.currentQualityLevels[i].label === label) {
        chosenLevelIndex = i;
        break;
      }
    }

    // return early if there are no sources in the desired quality, the quality setting wil still have been updated
    if (chosenLevelIndex === -1) {
      this.logger.warn(`Levels: The change in quality could not be completed, there are no sources for the quality '${label}'`);
      return false;
    }

    // update the current quality index
    this.currentQualityIndex = chosenLevelIndex;

    // let the player know that the current quality level has changed
    this.trigger({
      type: LevelsController.EVENT_SELECT,
      level: this.currentQualityLevels[this.currentQualityIndex]
    });
  }

  /**
   * Change the current list of available quality levels
   *
   * @param {array} levels A list of quality levels
   */
  setQualityLevels (levels) {
    // determine the current level
    const label = this.getQualityLevel();

    // const order = getQualityOrder((this.getCurrentLevel() || {}).label, defaultQualityOrder, label);
    const sorter = qualitySorter(defaultQualityOrder);
    const sorted = levels.slice(0).sort(sorter);

    // holds the index of the chosen level
    let chosenLevelIndex = -1;

    // find the current active quality in the list
    for (let i = 0, l = sorted.length; i < l; i++) {
      if (sorted[i].label === label && sorted[i].sources && sorted[i].sources.length > 0) {
        chosenLevelIndex = i;
        break;
      }
    }

    // if the current quality does not have a source, find the next best quality level
    if (chosenLevelIndex === -1) {
      for (let i = 0, l = sorted.length; i < l; i++) {
        if (sorted[i].sources && sorted[i].sources.length > 0) {
          const nextLabel = sorted[i].label;
          chosenLevelIndex = i;
          this.logger.warn(`Levels: The new levels list does not have a level for the current quality level setting '${label}', falling back to '${nextLabel}'`);
          break;
        }
      }
    }

    // update the list of levels
    this.currentQualityLevels = sorted;
    this.currentQualityIndex = chosenLevelIndex;

    this.trigger({
      type: LevelsController.EVENT_CHANGE,
      levels: this.getQualityLevels(),
      level: this.currentQualityLevels[this.currentQualityIndex]
    });
  }

  /**
   * Changes the current list of available quality levels by playlist item
   *
   * @param {object} playlistItem The loaded playlist item
   */
  setQualityLevelsFromPlaylistItem (playlistItem) {
    // get a list of audio sources that are supported by the current environment
    let supportedSources = getSupportedSources.call(this, playlistItem.audio);

    // if no supported sources are found, fallback to using the sources as they appear in the playlist item
    if (supportedSources.length === 0) {
      this.logger.warn('Levels: Unable to find any supported sources, using the sources as they appear in the playlist item');
      supportedSources = playlistItem.audio;
    }

    const qualityLevels = getQualityLevels.call(this, supportedSources);

    // update the available quality levels
    this.setQualityLevels(qualityLevels);
  }
}

LevelsController.EVENT_CHANGE = 'change';
LevelsController.EVENT_SELECT = 'select';
